import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BookOpen, Instagram, Music2, Youtube, Clapperboard, Sparkles,
  LayoutGrid, Mail, DollarSign, Star, ShieldCheck,
  Lock, Check, ArrowRight, Zap, TrendingUp, Users,
  EyeOff, ShoppingBag, Clock, Rocket, X, Palette, Store, Award,
} from "lucide-react";
import { startCheckout } from "@/lib/checkout";
import { useSeo } from "@/lib/useSeo";
import { getPost } from "@/data/blogPosts";
import BonusSection from "@/components/BonusSection";
import NewsletterSection from "@/components/NewsletterSection";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import SoldTodayBadge from "@/components/SoldTodayBadge";

const LOGO = "/assets/logo.png";
const HERO_BG = "/assets/hero-bg.jpg";
const ACCENT_1 = "/assets/accent-1.jpg";
const ACCENT_2 = "/assets/accent-2.jpg";

const EBOOKS = [
  { icon: BookOpen, title: "The Ultimate Faceless Marketing Guide", tag: "Core Playbook", pages: 121, desc: "A step-by-step, in-depth blueprint to launching your very own faceless digital marketing business." },
  { icon: Youtube, title: "Faceless Youtube Automation For Passive Profits", tag: "Passive Income", pages: 128, desc: "Comprehensive guide to building faceless YouTube automation channels using AI." },
  { icon: Music2, title: "Faceless TikTok Guide for Digital Marketing", tag: "TikTok", pages: 99, desc: "A done-for-you guide to faceless marketing and going viral on TikTok." },
  { icon: Instagram, title: "The Ultimate Faceless Instagram Guide", tag: "Instagram", pages: 69, desc: "Navigate and grow a faceless Instagram account for digital marketing success." },
  { icon: Clapperboard, title: "Mastering Your Faceless Instagram Reels", tag: "Reels", pages: 47, desc: "Learn how to optimize your faceless account with high-impact, scroll-stopping reels." },
  { icon: Sparkles, title: "Chat GPT And Canva", tag: "AI & Design", pages: 76, desc: "A beginner's guide to crafting digital products using ChatGPT and Canva from A to Z." },
  { icon: TrendingUp, title: "Faceless Digital Marketing", tag: "Blueprint", pages: 43, desc: "Discover 19 proven sales strategies tailored for Instagram and email marketing." },
  { icon: LayoutGrid, title: "Mastering Etsy and Pinterest MRR", tag: "Master Resell Rights", pages: 63, desc: "A comprehensive guide to selling digital products on Etsy and Pinterest." },
  { icon: Users, title: "Mastering The World Of Social Media", tag: "Social Media", pages: 84, desc: "A beginner's guide to building and growing your own online presence." },
  { icon: Mail, title: "eMail Marketing For Beginners", tag: "Email", pages: 67, desc: "Go from novice to expert by mastering the fundamentals of email marketing for digital success." },
  { icon: DollarSign, title: "TikTok Money Guide", tag: "Monetize", pages: 60, desc: "This complete 2026 TikTok Money Guide breaks down 7 proven ways creators are earning today." },
];

const REVIEWS = [
  {
    name: "Mike",
    handle: "Etsy Digital Seller",
    text: "I spent weeks hunting for the best digital products to sell and nothing came close to this. The ebooks were ready to list and fully editable in Canva, so I launched my faceless Etsy shop the same weekend — no camera, no stress.",
  },
  {
    name: "Charmaine",
    handle: "Faceless Content Creator",
    text: "I've tried a few resell-rights packs before, but this is easily the best MRR bundle I've come across. Being able to rebrand everything as my own and keep 100% of it made starting my faceless page feel completely doable.",
  },
  {
    name: "Delmar",
    handle: "New Faceless Creator",
    text: "As a total beginner, this digital marketing ebook bundle finally made everything click. The 350 hooks and ready-to-post reels meant I actually had something to publish instead of staring at a blank screen.",
  },
];

const FAQS = [
  {
    q: "What is Social Media Combo?",
    a: "Social Media Combo is a digital publisher of faceless social-media marketing ebooks, guides, and ready-to-post reels that help creators grow and monetize anonymous brands on Instagram, TikTok and YouTube — no filming, no face required.",
  },
  {
    q: "What does Social Media Combo sell?",
    a: "We sell The Faceless Marketing Bundle — a set of 11 in-depth digital ebooks plus 3 free bonuses (350 viral hooks, 100 hustle-mindset reels and 200 luxe-life reels) for a one-time price of $27.",
  },
  {
    q: "What is faceless social media marketing?",
    a: "Faceless marketing is a strategy for building and monetizing a social media brand without ever showing your face — using reels, quotes, voiceovers, and aesthetic content instead of appearing on camera.",
  },
  {
    q: "Do I need to show my face or film videos?",
    a: "No. Everything in the bundle is designed for faceless creators. You can grow an audience and sell digital products without filming yourself or appearing on camera.",
  },
  {
    q: "What are Master Resell Rights (MRR)?",
    a: "Master Resell Rights let you rebrand and resell the ebooks as your own and keep 100% of the profit. The bundle includes MRR, so you can use the content personally or turn it into your own product.",
  },
  {
    q: "Is there a money-back guarantee?",
    a: "Yes. Every purchase is backed by a 30-day, no-questions-asked money-back guarantee.",
  },
  {
    q: "How can I make money online with The Faceless Marketing Bundle?",
    a: "The bundle teaches you how to make money online and build passive income by selling digital products, growing faceless social media pages, and monetizing platforms like Instagram, TikTok, and YouTube — all without showing your face.",
  },
  {
    q: "Can I sell these ebooks and PDFs on Etsy?",
    a: "Yes. Every ebook includes Master Resell Rights, so you can rebrand them and sell the PDFs on Etsy, Pinterest, or your own store and keep 100% of the profit. The bundle even includes a dedicated guide on mastering Etsy and Pinterest.",
  },
  {
    q: "Will this help me grow on Instagram, TikTok, and YouTube?",
    a: "Absolutely. You get step-by-step Instagram growth strategies, tips to get more likes organically, faceless reels frameworks, and 350 viral content hooks you can use to grow and monetize an audience on any platform.",
  },
];

const PROBLEMS = [
  "You want to earn online but don't want to show your face or be on camera.",
  "You're posting content but have nothing to actually sell.",
  "Every course costs thousands and leaves you more confused than before.",
  "You don't know where to start, what to post, or what to sell.",
];

const SOLUTIONS = [
  "Sell proven digital products completely faceless — no camera, ever.",
  "Get 11 done-for-you ebooks you own and can resell as your own.",
  "One $27 bundle with a clear, step-by-step system across every platform.",
  "350 viral hooks + 300 ready-to-post reels so you can publish today.",
];

const PERSONAS = [
  { icon: EyeOff, title: "The camera-shy creator", desc: "You want to make money online but you'll never show your face or go on camera." },
  { icon: Clock, title: "The busy side-hustler", desc: "A 9-to-5er, student, or parent who needs a low-cost, low-risk way to start earning." },
  { icon: ShoppingBag, title: "The digital product seller", desc: "Ready to sell on Etsy, Stan Store & Beacons — without creating products from scratch." },
  { icon: Rocket, title: "The stuck creator", desc: "You're posting but not growing. You need proven hooks, reels, and a system that works." },
];

const WHY_US = [
  { icon: Store, title: "Ready to sell everywhere", desc: "Upload and start selling instantly on Beacons, Stan Store & Etsy — your storefront can be live today." },
  { icon: Palette, title: "Fully editable in Canva", desc: "Every PDF opens in Canva. Rebrand the covers, colors, and text to make it 100% your own in minutes." },
  { icon: Award, title: "Master Resell Rights included", desc: "Rebrand the ebooks as your product and keep 100% of every sale. It's inventory you own." },
  { icon: Zap, title: "Post today, not someday", desc: "350 viral hooks + 300 faceless reels mean you never stare at a blank content calendar again." },
  { icon: LayoutGrid, title: "A complete system", desc: "11 guides covering Instagram, TikTok, YouTube, Etsy & Pinterest — not one random ebook." },
  { icon: ShieldCheck, title: "Zero-risk to try", desc: "Just $27, instant delivery, and a 30-day no-questions-asked money-back guarantee." },
];

const START_HERE = [
  "best-mrr-bundle-how-to-resell-digital-products",
  "how-to-sell-pdfs-on-etsy",
  "youtube-automation-guide-for-beginners-faceless",
].map(getPost).filter(Boolean);


const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Landing() {
  const [loading, setLoading] = useState(false);

  useSeo({
    title: "Social Media Combo - Get 300 Faceless Marketing Reels Bonus Now!",
    description:
      "The Faceless Digital Marketing Bundle — 11 premium ebooks + 3 free bonuses to sell digital products and monetize your social media going faceless. Instant access, just $27.",
    path: "/",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("canceled")) {
      toast.error("Checkout canceled — your bundle is still waiting for you.");
    }
  }, []);

  const handleBuy = async () => {
    try {
      setLoading(true);
      await startCheckout();
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong starting checkout. Please try again.");
      setLoading(false);
    }
  };

  const GoldButton = ({ children, testid, className = "" }) => (
    <button
      data-testid={testid}
      onClick={handleBuy}
      disabled={loading}
      className={`btn-gold inline-flex items-center justify-center gap-3 px-10 py-5 text-base sm:text-lg ${className}`}
    >
      {loading ? "Processing…" : children}
      {!loading && <ArrowRight className="w-5 h-5" />}
    </button>
  );

  return (
    <div className="theme-warm relative bg-[#FFF9F2] text-[#232323] overflow-x-hidden">
      <ExitIntentPopup />
      {/* ===== Header ===== */}
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-[#FFF9F2]/80 border-b border-black/10"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <a href="/" data-testid="header-home-link" className="flex items-center gap-3">
            <img src={LOGO} alt="The Faceless Bundle logo — faceless digital marketing ebooks to monetize social media and earn passive income" className="w-9 h-9 object-contain" />
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#F59E0B]">
                Social Media Combo
              </span>
              <span className="font-serif-display text-lg sm:text-xl tracking-[0.25em] font-semibold text-[#232323]">
                THE FACELESS BUNDLE<span className="text-[#F59E0B]">.</span>
              </span>
            </div>
          </a>
          <button
            data-testid="header-cta-button"
            onClick={handleBuy}
            disabled={loading}
            className="hidden sm:inline-flex items-center gap-2 border border-[#F59E0B] text-[#F59E0B] px-6 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#F59E0B] hover:text-black transition-colors"
          >
            Get Access — $27
          </button>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center pt-28 pb-20 grain"
      >
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="Faceless content creator workspace with a laptop and a cup of coffee — start a faceless digital marketing business" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFF9F2] via-[#FFF9F2]/85 to-[#FFF9F2]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FFF9F2]/90 via-transparent to-[#FFF9F2]/25" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="max-w-3xl">
            <motion.div
              initial="hidden" animate="show" variants={fadeUp}
              className="inline-flex items-center gap-2 border border-[#F59E0B]/40 bg-[#F59E0B]/5 px-4 py-2 mb-8"
            >
              <Users className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-xs uppercase tracking-[0.2em] text-[#F59E0B]">
                2,000+ creators already inside
              </span>
            </motion.div>

            <motion.h1
              initial="hidden" animate="show" custom={1} variants={fadeUp}
              className="font-serif-display text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight"
            >
              The <span className="gold-shimmer">Faceless Digital Marketing Bundle</span> — Sell Online Without Ever Showing Your Face
            </motion.h1>

            <motion.p
              initial="hidden" animate="show" custom={2} variants={fadeUp}
              className="mt-8 text-lg sm:text-xl text-[#353535] leading-relaxed max-w-2xl"
            >
              Get the complete system — 11 ebooks, 350 viral hooks, and 300 ready-to-post reels —
              to grow and monetize a faceless brand on Instagram, TikTok &amp; YouTube. No filming,
              no editing, no face required.
            </motion.p>

            <motion.p
              initial="hidden" animate="show" custom={3} variants={fadeUp}
              className="mt-4 text-sm text-[#666666] leading-relaxed max-w-2xl"
            >
              Social Media Combo is a digital publisher of faceless social-media marketing ebooks,
              guides, and ready-to-post reels that help creators grow and monetize anonymous brands
              on Instagram, TikTok &amp; YouTube — no filming, no face required.
            </motion.p>

            <motion.div
              initial="hidden" animate="show" custom={3} variants={fadeUp}
              className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6"
            >
              <GoldButton testid="hero-cta-button">
                Get Your Faceless Content System Now
              </GoldButton>
              <div className="flex items-center gap-2 text-sm text-[#666666]">
                <ShieldCheck className="w-5 h-5 text-[#F59E0B]" />
                100% Money-Back Guarantee
              </div>
            </motion.div>

            <motion.div
              initial="hidden" animate="show" custom={3} variants={fadeUp}
              className="mt-6"
            >
              <SoldTodayBadge testid="hero-sold-today" />
            </motion.div>

            <motion.div
              initial="hidden" animate="show" custom={4} variants={fadeUp}
              className="mt-16 flex flex-wrap gap-x-10 gap-y-4 text-sm text-[#666666]"
            >
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#F59E0B]" /> 11 premium ebooks</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#F59E0B]" /> Instant digital delivery</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#F59E0B]" /> Master Resell Rights (MRR) included</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#F59E0B]" /> Buy. Learn. Rebrand.</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== Stats strip ===== */}
      <section className="border-y border-[#EFE3D5] bg-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-zinc-900">
          {[
            { icon: Zap, stat: "11", label: "In-depth ebooks" },
            { icon: Users, stat: "2,000+", label: "Active creators" },
            { icon: TrendingUp, stat: "300+", label: "Viral hooks included" },
          ].map((s, i) => (
            <div key={i} className="py-10 flex items-center justify-center gap-4">
              <s.icon className="w-8 h-8 text-[#F59E0B]" />
              <div>
                <div className="font-serif-display text-3xl font-bold">{s.stat}</div>
                <div className="text-xs uppercase tracking-widest text-[#666666]">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Problem / Solution ===== */}
      <section data-testid="problem-solution-section" className="relative py-24 md:py-32 bg-[#FFFFFF] border-b border-[#EFE3D5]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-sm uppercase tracking-[0.25em] text-[#F59E0B]">Getting views but no income?</span>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 leading-tight">
              You don't have a traffic problem. You have a <span className="gold-shimmer">product problem</span>.
            </h2>
            <p className="mt-6 text-[#666666]">
              Most people quit not because they can't get views — but because they have nothing to sell and no system to follow. This bundle fixes both.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={fadeUp}
              data-testid="problem-card"
              className="border border-red-200 bg-[#FEF3F2] p-8"
            >
              <h3 className="font-serif-display text-xl font-semibold text-[#232323]">The struggle</h3>
              <ul className="mt-6 space-y-4">
                {PROBLEMS.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#666666] leading-relaxed">
                    <X className="w-5 h-5 text-red-500/70 shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={1} variants={fadeUp}
              data-testid="solution-card"
              className="border border-[#F59E0B]/40 bg-[#FFFBEB] p-8"
            >
              <h3 className="font-serif-display text-xl font-semibold text-[#232323]">The fix — The Faceless Bundle</h3>
              <ul className="mt-6 space-y-4">
                {SOLUTIONS.map((s, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#353535] leading-relaxed">
                    <Check className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" /> {s}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== What You Get ===== */}
      <section data-testid="ebooks-section" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="relative"
            >
              <div className="absolute -inset-6 bg-[#F59E0B]/10 blur-3xl rounded-full" />
              <img
                src="/assets/faceless-vault.jpg"
                alt="The Complete Faceless Vault by Social Media Combo — 11 premium ebooks plus 3 bonus guides, including The Ultimate Faceless Marketing Guide, Faceless YouTube Automation, Faceless TikTok Guide, Ultimate Instagram Guide, Mastering Reels, Chat GPT and Canva, Faceless Digital Marketing, Etsy & Pinterest MRR, Mastering the World of Social Media, Email Marketing, and TikTok Money Guide"
                data-testid="bundle-banner-image"
                className="relative z-10 w-full max-w-lg mx-auto drop-shadow-[0_20px_60px_rgba(0,0,0,0.8)]"
              />
            </motion.div>

            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              custom={1} variants={fadeUp}
            >
              <span className="text-sm uppercase tracking-[0.25em] text-[#F59E0B]">The Faceless Digital Marketing Bundle</span>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 leading-tight">
                The Complete 11-Ebook Faceless Digital Marketing Blueprint
              </h2>
              <p className="mt-6 text-lg text-[#666666]">
                Everything you need to launch, grow, and monetize a faceless content empire —
                across every major platform. Instantly downloadable, yours for life. Whether 
                you're a beginner looking to learn the ropes of faceless digital marketing or 
                an experienced marketer aiming to diversify your income streams, this bundle 
                offers the tools and knowledge you need to succeed. It's not just an educational 
                resource; it's a complete business opportunity. Rebrand the content to fit your 
                personal brand and enjoy 100% of the profits from your sales!! Or give them away 
                for free to your premiun subscribers. Everyone loves a Free ebook!
              </p>
              <div className="mt-8 flex items-center gap-4">
                <span className="font-serif-display text-2xl font-bold line-through text-[#8A8A8A]">$197</span>
                <span className="font-serif-display text-4xl font-bold gold-shimmer">$27</span>
                <span className="text-xs uppercase tracking-widest text-[#666666] border border-[#EFE3D5] px-3 py-1">
                  86% off
                </span>
              </div>
              <button
                data-testid="ebooks-header-cta-button"
                onClick={handleBuy}
                disabled={loading}
                className="btn-gold mt-8 inline-flex items-center gap-3 px-8 py-4 text-sm"
              >
                {loading ? "Processing…" : "Get All 11 Guides"}
                {!loading && <ArrowRight className="w-5 h-5" />}
              </button>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {EBOOKS.map((book, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
                custom={i % 3} variants={fadeUp}
                data-testid={`ebook-card-${i}`}
                className="group relative bg-[#FFFFFF] border border-[#EFE3D5] p-7 hover:border-[#F59E0B]/50 transition-colors duration-300"
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 flex items-center justify-center border border-[#EFE3D5] bg-[#FFF7ED] group-hover:border-[#F59E0B]/40 transition-colors">
                    <book.icon className="w-6 h-6 text-[#F59E0B]" />
                  </div>
                  <span className="font-serif-display text-2xl text-[#C9BBA8] group-hover:text-[#F59E0B]/60 transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-6 text-lg font-semibold leading-snug">{book.title}</h3>
                <p className="mt-3 text-sm text-[#666666] leading-relaxed">{book.desc}</p>
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-xs uppercase tracking-widest text-[#F59E0B]/80">
                    {book.tag}
                  </span>
                  <span className="text-xs text-[#8A8A8A]">·</span>
                  <span className="text-xs text-[#666666]">{book.pages} pages</span>
                </div>
              </motion.div>
            ))}

            {/* Value CTA card fills the 12th grid slot */}
            <div className="relative overflow-hidden border border-[#F59E0B]/40 bg-[#FFF7ED] p-7 flex flex-col justify-between">
              <div className="relative z-10">
                <span className="text-xs uppercase tracking-widest text-[#F59E0B]">Total value</span>
                <div className="mt-3 flex items-baseline gap-3">
                  <span className="font-serif-display text-4xl font-bold line-through text-[#8A8A8A]">$197</span>
                  <span className="font-serif-display text-5xl font-bold gold-shimmer">$27</span>
                </div>
              </div>
              <button
                data-testid="ebooks-cta-button"
                onClick={handleBuy}
                disabled={loading}
                className="btn-gold relative z-10 mt-6 w-full py-4 text-sm"
              >
                Claim The Bundle
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Who This Is For ===== */}
      <section data-testid="who-for-section" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-sm uppercase tracking-[0.25em] text-[#F59E0B]">Is this for you?</span>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 leading-tight">
              Built for creators who want income — not an audience
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PERSONAS.map((p, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }} custom={i % 4} variants={fadeUp}
                data-testid={`persona-card-${i}`}
                className="bg-[#FFFFFF] border border-[#EFE3D5] p-7 hover:border-[#F59E0B]/50 transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-[#EFE3D5] bg-[#FFF7ED]">
                  <p.icon className="w-6 h-6 text-[#F59E0B]" />
                </div>
                <h3 className="mt-5 font-serif-display text-lg font-semibold leading-snug">{p.title}</h3>
                <p className="mt-3 text-sm text-[#666666] leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Testimonials ===== */}
      <section data-testid="testimonials-section" className="relative py-24 md:py-32 bg-[#FFFFFF] border-y border-[#EFE3D5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-sm uppercase tracking-[0.25em] text-[#F59E0B]">Why people love it</span>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4">
              What Faceless Creators Are Saying
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {REVIEWS.map((r, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
                custom={i} variants={fadeUp}
                data-testid={`testimonial-card-${i}`}
                className="bg-[#FFFFFF] border border-[#EFE3D5] p-8 flex flex-col"
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-5 h-5 text-[#F59E0B]" fill="#F59E0B" />
                  ))}
                </div>
                <p className="text-[#353535] leading-relaxed flex-1">“{r.text}”</p>
                <div className="mt-8 flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#FFF7ED] border border-[#F59E0B]/40 font-serif-display text-xl text-[#F59E0B]">
                    {r.name[0]}
                  </div>
                  <div>
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-xs uppercase tracking-widest text-[#666666]">{r.handle}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Bonuses ===== */}
      <BonusSection />

      {/* ===== Why Buy From Us ===== */}
      <section data-testid="why-us-section" className="relative py-24 md:py-32 bg-[#FFFFFF] border-y border-[#EFE3D5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-sm uppercase tracking-[0.25em] text-[#F59E0B]">Why Social Media Combo</span>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 leading-tight">
              Not just ebooks — a <span className="gold-shimmer">business in a download</span>
            </h2>
            <p className="mt-6 text-[#666666]">
              This isn't another PDF you'll never open. It's a ready-to-sell product line you can launch on Beacons, Stan Store, and Etsy — and edit in Canva to make entirely your own.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_US.map((w, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }} custom={i % 3} variants={fadeUp}
                data-testid={`why-us-card-${i}`}
                className="bg-[#FFFFFF] border border-[#EFE3D5] p-7 hover:border-[#F59E0B]/50 transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-[#EFE3D5] bg-[#FFF7ED]">
                  <w.icon className="w-6 h-6 text-[#F59E0B]" />
                </div>
                <h3 className="mt-5 font-serif-display text-lg font-semibold leading-snug">{w.title}</h3>
                <p className="mt-3 text-sm text-[#666666] leading-relaxed">{w.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-14 text-center">
            <GoldButton testid="why-us-cta-button">Start Selling Digital Products — $27</GoldButton>
          </div>
        </div>
      </section>

      {/* ===== Trust & Payment Footer ===== */}
      <section data-testid="checkout-section" className="relative py-24 md:py-32 grain overflow-hidden">
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#FFF9F2] via-[#FFE8D6] to-[#FFF9F2]" />

        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
            Ready to Monetize Your Social Media — Faceless?
          </h2>

          <div className="mt-8 flex items-center justify-center gap-4">
            <span className="font-serif-display text-3xl font-bold line-through text-[#8A8A8A]">$197</span>
            <span className="font-serif-display text-6xl font-bold gold-shimmer">$27</span>
          </div>
          <p className="mt-3 text-sm uppercase tracking-widest text-[#666666]">One-time payment · Lifetime access</p>

          <div className="mt-12">
            <GoldButton testid="footer-cta-button" className="w-full sm:w-auto">
              Buy Now — Get Instant Access
            </GoldButton>
            <div className="mt-5 flex justify-center">
              <SoldTodayBadge testid="footer-sold-today" />
            </div>
          </div>

          {/* Trust badges */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
            <div className="flex items-start gap-4 border border-[#EFE3D5] bg-[#FFFFFF]/80 p-6">
              <ShieldCheck className="w-8 h-8 text-[#F59E0B] shrink-0" />
              <div>
                <div className="font-semibold">100% Money-Back</div>
                <div className="text-sm text-[#666666]">30-day guarantee, no questions asked.</div>
              </div>
            </div>
            <div className="flex items-start gap-4 border border-[#EFE3D5] bg-[#FFFFFF]/80 p-6">
              <Lock className="w-8 h-8 text-[#F59E0B] shrink-0" />
              <div>
                <div className="font-semibold">Secure Checkout</div>
                <div className="text-sm text-[#666666]">Encrypted payment powered by Stripe.</div>
              </div>
            </div>
            <div className="flex items-start gap-4 border border-[#EFE3D5] bg-[#FFFFFF]/80 p-6">
              <Zap className="w-8 h-8 text-[#F59E0B] shrink-0" />
              <div>
                <div className="font-semibold">Instant Delivery</div>
                <div className="text-sm text-[#666666]">Download all 11 ebooks immediately.</div>
              </div>
            </div>
          </div>

          {/* Secure checkout — Stripe */}
          <div className="mt-12 flex items-center justify-center gap-2 text-sm text-[#666666]">
            <Lock className="w-4 h-4" />
            Checkout is secure and works instantly. Powered by
            <span className="font-extrabold tracking-tight text-[#635BFF]">stripe</span>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section data-testid="faq-section" className="relative py-24 md:py-32 bg-[#FFFFFF] border-t border-[#EFE3D5]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-sm uppercase tracking-[0.25em] text-[#F59E0B]">FAQ</span>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
                custom={i % 3} variants={fadeUp}
                data-testid={`faq-item-${i}`}
                className="border border-[#EFE3D5] bg-[#FFFFFF] p-6 hover:border-[#F59E0B]/40 transition-colors"
              >
                <h3 className="font-serif-display text-lg font-semibold text-[#232323] flex items-start gap-3">
                  <span className="text-[#F59E0B] shrink-0">Q.</span> {f.q}
                </h3>
                <p className="mt-3 text-sm text-[#666666] leading-relaxed pl-7">{f.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Start Here: Free Guides ===== */}
      <section data-testid="start-here-section" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-sm uppercase tracking-[0.25em] text-[#F59E0B]">Start here — free guides</span>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 leading-tight">
              Not ready to buy? Read these first
            </h2>
            <p className="mt-6 text-[#666666]">
              Free, no-fluff guides on selling digital products, resell rights, and faceless income.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {START_HERE.map((post, i) => (
              <motion.a
                key={post.slug}
                href={`/blog/${post.slug}`}
                data-testid={`start-here-card-${i}`}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }} custom={i} variants={fadeUp}
                className="group flex flex-col bg-[#FFFFFF] border border-[#EFE3D5] hover:border-[#F59E0B]/50 transition-colors overflow-hidden"
              >
                <div className="aspect-[16/9] overflow-hidden bg-[#F5EFE7]">
                  <img
                    src={post.cover}
                    alt={post.coverAlt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-xs uppercase tracking-widest text-[#F59E0B]">{post.tag}</span>
                  <h3 className="mt-3 font-serif-display text-lg font-semibold leading-snug group-hover:text-[#F59E0B] transition-colors">
                    {post.title}
                  </h3>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-[#F59E0B] mt-auto pt-4">
                    Read the guide <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </motion.a>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a href="/blog" data-testid="start-here-all-link" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#4338CA] hover:text-[#3730A3] transition-colors">
              View all guides <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ===== Newsletter / Free Ebook ===== */}
      <NewsletterSection />

      {/* ===== Bottom line ===== */}
      <footer className="border-t border-[#EFE3D5] py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#8A8A8A]">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="The Faceless Bundle logo — 11 faceless digital marketing ebooks to monetize social media and earn passive income" className="w-6 h-6 object-contain" />
            <span className="font-serif-display tracking-widest">THE FACELESS BUNDLE.</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <div className="flex items-center gap-6">
              <a href="/blog" data-testid="footer-blog-link" className="hover:text-[#4338CA] transition-colors">Blog</a>
              <a href="/bonuses" data-testid="footer-bonus-link" className="hover:text-[#4338CA] transition-colors">Bonus</a>
              <a href="/about" data-testid="footer-about-link" className="hover:text-[#4338CA] transition-colors">About</a>
              <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-[#4338CA] transition-colors">Privacy</a>
              <a href="/terms" data-testid="footer-terms-link" className="hover:text-[#4338CA] transition-colors">Terms</a>
            </div>
            <span>© {new Date().getFullYear()} socialmediacombo.com — All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
